import { FormEvent, RefObject, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetSTMembershipsQuery } from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"

import { convertDataToOptions } from "utils/convertData"
import { isPartnersEnabledPlan } from "utils/isPartnersEnabledPlan"
import {
  contractorSchema,
  contractorSTIntegrationSchema,
  onFormValidate,
  validationInitialState,
} from "utils/onValidate"
import type { ContractorPartnersFormHandle } from "components/ContractorPartnersForm"

import DetailsSection from "./DetailsSection"
import InfoSection from "./InfoSection"
import IntegrationSection from "./IntegrationSection"
import { PartnersSection } from "./PartnersSection"
import SubscriptionSection from "./SubscriptionSection"
import prepareFormData from "./utils/prepareFormData"

import styles from "./index.module.scss"

interface Props {
  isCreate?: boolean
  contractor: UITypes.Contractor
  partnersFormRef: RefObject<ContractorPartnersFormHandle | null>
  onSubmit: (data: FormData, contractor: UITypes.Contractor) => void | Promise<void>
}

export const ContractorModalContent = ({ contractor, isCreate, partnersFormRef, onSubmit }: Props) => {
  const [triggerGetSTMemberships, { isFetching: isSTMembershipsFetching, data: membershipsData }] =
    useLazyGetSTMembershipsQuery()
  const [tenantIDStatus, setTenantIDStatus] = useState<UITypes.TenantIDStautus>("check")
  const [contractorMembershipTypes, setContractorMembershipTypes] = useState<UITypes.Option[]>(
    contractor?.membershipTypes?.map(item => ({
      value: item.st_id,
      label: item.st_name,
    })) || [],
  )
  const [inputValues, setInputValues] = useState(contractor!)
  const [error, setError] = useState(validationInitialState)
  const currentZip = useAppSelector(state => state.tempData.currentZip)

  const membershipTypesList: UITypes.MembershipType[] = useMemo(() => {
    if (membershipsData) {
      return membershipsData.data.map(item => ({
        st_id: item.id,
        st_name: item.name,
        st_active: item.active,
        st_created_at: item.createdOn.replace("T", " ").split(".")[0],
        st_updated_at: item.modifiedOn.replace("T", " ").split(".")[0],
      }))
    }

    return []
  }, [membershipsData])

  useEffect(() => {
    if (tenantIDStatus === "pass") {
      const fetchData = async () => {
        try {
          await triggerGetSTMemberships({
            client_id: inputValues.stClientID,
            client_secret: inputValues.stClientSecret,
            app_key: inputValues.stAppKey,
            tenant_id: inputValues.stTenant,
          })
        } catch (error) {
          console.error(error)
        }
      }

      fetchData()
    }
  }, [tenantIDStatus])

  const onInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.currentTarget

    setInputValues(prevState => {
      return { ...prevState, [name]: type === "checkbox" ? checked : value }
    })
  }

  const stFieldsNotEmpty = () => {
    return (
      inputValues.stClientID?.length ||
      inputValues.stClientSecret?.length ||
      inputValues.stAppKey?.length ||
      inputValues.stTenant?.length
    )
  }

  const handleSubmit = async (event: FormEvent<HTMLDivElement>) => {
    event.preventDefault()

    if (!onFormValidate(inputValues, contractorSchema, setError, true)) return

    if (
      (contractor?.isSTChecked === "1" || stFieldsNotEmpty()) &&
      !onFormValidate(inputValues, contractorSTIntegrationSchema, setError, true)
    ) {
      return
    }

    if (isCreate && !inputValues.image) {
      setError({ field: "logo", message: "Logo is required" })
      return
    }

    if (stFieldsNotEmpty() && tenantIDStatus !== "pass") {
      toast.error("Please check Tenant ID")
      return
    }

    if (partnersFormRef.current && !partnersFormRef.current.validatePartners()) return

    await onSubmit(
      prepareFormData(
        inputValues,
        tenantIDStatus,
        membershipTypesList.filter(item => contractorMembershipTypes.some(type => type.value === item.st_id)),
      ),
      inputValues,
    )
  }

  useEffect(() => {
    if (currentZip?.city) {
      const city = convertDataToOptions({ data: [currentZip.city] }, "id", "name")
      const state = convertDataToOptions({ data: [currentZip.state] }, "id", "name")
      const country = convertDataToOptions({ data: [currentZip.country] }, "id", "name")
      const suburb = convertDataToOptions(
        { data: currentZip.suburbs?.length > 0 ? [currentZip.suburbs[0]] : null },
        "id",
        "name",
      )

      if (city && state) {
        setInputValues(prev => ({
          ...prev,
          city,
          state,
          country,
          suburb,
        }))
      }
    }
  }, [currentZip])

  return (
    <>
      <div className={styles.saveButton} onClick={handleSubmit}>
        Save
      </div>
      <div className={styles.contractorContainer}>
        <DetailsSection
          inputValues={inputValues}
          error={error}
          onInputChange={onInputChange}
          setInputValues={setInputValues}
        />
        <SubscriptionSection
          inputValues={inputValues}
          onInputChange={onInputChange}
          setInputValues={setInputValues}
          isCreate={isCreate}
        />
        <InfoSection
          inputValues={inputValues}
          error={error}
          setError={setError}
          onInputChange={onInputChange}
          setInputValues={setInputValues}
        />
        {isPartnersEnabledPlan(inputValues.subscriptionPlan) && (
          <PartnersSection ref={partnersFormRef} contractor={contractor} isCreate={isCreate} />
        )}
        <IntegrationSection
          inputValues={inputValues}
          error={error}
          setError={setError}
          onInputChange={onInputChange}
          tenantIDStatus={tenantIDStatus}
          setTenantIDStatus={setTenantIDStatus}
          contractorMembershipTypes={contractorMembershipTypes}
          setContractorMembershipTypes={setContractorMembershipTypes}
          membershipTypesList={membershipTypesList}
          isSTMembershipsFetching={isSTMembershipsFetching}
        />
      </div>
    </>
  )
}
